import { FC, useCallback, useEffect, ReactNode } from 'react';
import ColorsRow from '../../components/ColorsRow/ColorsRow';
import { COLOR_ELEMENT_IDENTIFIER } from '../../screens/ConsumerChannelsConfigScreen/constants';
import {
  IConfigKioskColorRow,
  IConfigKioskColors,
  IConfigKioskColorsValidation,
  TConfigKioskColorField,
} from '../../screens/KioskConfigScreen/types';
import {
  IConfigMobileColorRow,
  IConfigMobileColors,
  IConfigMobileColorsValidation,
  TConfigMobileColorField,
} from '../../screens/MobileConfigScreen/types';
import { IConfigWebColorRow, IConfigWebColors, IConfigWebColorsValidation, TConfigWebColorField } from '../../screens/WebConfigScreen/types';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import Colorful from 'src/view/components/Colorful/Colorful';
import Icon from 'src/view/components/Controls/Icon/Icon';
import './style.scss';
import Button from '~/view/components/Controls/ButtonComponent/Button';
import { BUTTON_TITLE } from '~/constants/main';

type TRow = IConfigMobileColorRow | IConfigWebColorRow | IConfigKioskColorRow;
type TValues = IConfigMobileColors | IConfigWebColors | IConfigKioskColors;
type TField = TConfigMobileColorField | TConfigWebColorField | TConfigKioskColorField;
type TFocusField = keyof IConfigMobileColors | keyof IConfigWebColors | keyof IConfigKioskColors | null;
type TValidation = IConfigMobileColorsValidation | IConfigWebColorsValidation | IConfigKioskColorsValidation;

interface IProps {
  rows: TRow[];
  values: TValues;
  focusField: TFocusField;
  focusFieldValue: string;
  changeColors: (value: any) => void;
  setFocusField: (field: any) => void;
  submit?: () => void;
  validation: TValidation;
  children?: ReactNode;
}

const ColorsBlock: FC<IProps> = ({ rows, values, focusField, focusFieldValue, validation, ...props }: IProps): JSX.Element => {
  useEffect(() => {
    window.addEventListener('click', colorElementBlurListener);

    return () => window.removeEventListener('click', colorElementBlurListener);
  }, []);

  const colorElementBlurListener = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement | null;

    const idMatch: boolean = [COLOR_ELEMENT_IDENTIFIER.INPUT, COLOR_ELEMENT_IDENTIFIER.BOX, COLOR_ELEMENT_IDENTIFIER.HEX_PICKER].includes(
      target?.id || ''
    );
    const classNameMatch: boolean = Boolean(target?.className.includes(COLOR_ELEMENT_IDENTIFIER.CLASS_NAME));

    if (!idMatch && !classNameMatch) props.setFocusField(null);
  }, []);

  const changeColor = (field: TField) => (value: string | undefined) => {
    props.changeColors({ [field]: value });
  };

  return (
    <div className="d-flex flex-column flex-1 mt-4 gap-20">
      <div className="d-flex flex-row align-items gap-20">
        {!validation.isValid ? <Icon name={Icons.Warning} size={19} color={theme.colors.red} /> : null}
        <p className="colors-block__recommendation letter-spacing-5">{localeStore.t('please_enter_correct_format') + ': HEX: #111111'}</p>
      </div>

      <div className="colors-block__colors">
        <div className="colors-block__colors-list">
          {rows.map((color) => (
            <ColorsRow
              key={color.field}
              title={color.title}
              value={(values as any)[color.field]}
              onChange={changeColor(color.field)}
              onFocus={() => props.setFocusField(color.field)}
              isActive={focusField === color.field}
              validation={(validation as any)[color.field]}
            />
          ))}

          {props.children}

          {props.submit && <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={props.submit} className="colors-block__btn-save" />}
        </div>

        {focusField ? <Colorful id={COLOR_ELEMENT_IDENTIFIER.HEX_PICKER} color={focusFieldValue} onChange={changeColor(focusField)} /> : null}
      </div>
    </div>
  );
};

export default ColorsBlock;
